import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format seconds as M:SS or H:MM:SS.
 */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const END_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

/**
 * Format an access-period end as "May 22, 2026" in UTC. Shared by the
 * three surfaces that render the same value (account.tsx status,
 * dashboard.tsx countdown banner, email.server.ts trial-started
 * template). `null` passes through as `null` so callers can render
 * the "—" placeholder.
 */
export function formatEndDate(iso: string | null): string | null {
  if (!iso) return null;
  return END_DATE_FMT.format(new Date(iso));
}

/**
 * Format a view count YouTube-style: "1 view", "923 views", "1.2K views",
 * "3.4M views". Uses compact notation for counts >= 1000.
 */
export function formatViews(count: number): string {
  const n = Math.max(0, Math.floor(count));
  const label =
    n >= 1000
      ? new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n)
      : String(n);
  return `${label} ${n === 1 ? "view" : "views"}`;
}

/**
 * Format a payment amount from the append-only ledger. Both fields are
 * nullable because comp grants (and unrecorded amounts) are stored as
 * NULL — an amount with no unit is not a recorded amount, and a unit
 * with no amount is a unit attached to nothing. Renders "—" in either
 * case so the row still has a value, and falls through to `<minor> <ccy>`
 * for currencies not in the known set (defensive: only `bdt` and `usd`
 * are written today, but the schema accepts free-form currency text).
 */
export function formatAmount(
  minor: number | null,
  currency: string | null
): string {
  if (minor === null || currency === null) return "—";
  if (currency === "bdt") return `৳${(minor / 100).toFixed(2)} BDT`;
  if (currency === "usd") return `$${(minor / 100).toFixed(2)} USD`;
  return `${minor} ${currency.toUpperCase()}`;
}

const GMAIL_FAMILY_DOMAINS = new Set(["gmail.com", "googlemail.com"]);

/**
 * Canonicalizes an email address for identity purposes (R9). `+tag`
 * suffixes are stripped for every domain; dots in the local part are
 * collapsed only for Gmail-family domains (gmail.com, googlemail.com —
 * both normalize to gmail.com), since dots are meaningful identity on
 * most other domains.
 */
export function canonicalizeEmail(email: string): string {
  const lower = email.trim().toLowerCase();
  const atIndex = lower.lastIndexOf("@");
  if (atIndex === -1) return lower;

  let local = lower.slice(0, atIndex);
  let domain = lower.slice(atIndex + 1);

  // Search from index 1: a '+' at index 0 is an ordinary local-part
  // character, not a tag separator. Stripping there would collapse the
  // local part to "" and merge every leading-'+' address on a domain onto
  // one identity.
  const plusIndex = local.indexOf("+", 1);
  if (plusIndex > 0) local = local.slice(0, plusIndex);

  if (GMAIL_FAMILY_DOMAINS.has(domain)) {
    local = local.replace(/\./g, "");
    domain = "gmail.com";
  }

  return `${local}@${domain}`;
}
